const STORAGE_KEY_SEASON = "howoldhere:picker-season:";

const CHEVRON = `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" class="picker-chevron"><polyline points="4 6 8 10 12 6"/></svg>`;

function groupBySeason(show) {
  const map = new Map();
  for (const ep of show.episodes) {
    const key = ep.season;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(ep);
  }
  return map;
}

function loadStoredSeason(showId) {
  try {
    const v = localStorage.getItem(STORAGE_KEY_SEASON + showId);
    return v ? Number(v) : null;
  } catch { return null; }
}

function saveStoredSeason(showId, season) {
  try { localStorage.setItem(STORAGE_KEY_SEASON + showId, String(season)); } catch {}
}

function getInitialSeason(show, activeEpisodeId) {
  if (activeEpisodeId) {
    const ep = show.episodes.find(e => e.id === activeEpisodeId);
    if (ep) return ep.season;
  }
  const stored = loadStoredSeason(show.id);
  if (stored) return stored;
  return show.episodes[0]?.season ?? 1;
}

export function EpisodeList(show, activeEpisodeId = null, onSelect = null) {
  const seasonMap = groupBySeason(show);
  const seasons = [...seasonMap.keys()];

  let currentSeason = getInitialSeason(show, activeEpisodeId);
  let activeId = activeEpisodeId;

  const container = document.createElement("div");
  container.className = "ep-picker";

  // ── Season selector ────────────────────────────────────────
  const seasonWrap = document.createElement("div");
  seasonWrap.className = "picker-wrap";
  seasonWrap.innerHTML = `
    <button class="picker-btn" type="button" aria-expanded="false" aria-haspopup="listbox">
      <span class="picker-value"></span>
      ${CHEVRON}
    </button>
    <ul class="picker-list" role="listbox" aria-label="Season" hidden></ul>
  `;
  const seasonBtn = seasonWrap.querySelector(".picker-btn");
  seasonBtn.setAttribute("aria-label", "Choose season");
  const seasonList = seasonWrap.querySelector(".picker-list");
  const seasonValueEl = seasonWrap.querySelector(".picker-value");

  function seasonLabel() {
    return `Season ${currentSeason}`;
  }

  function buildSeasonOptions() {
    seasonList.innerHTML = seasons.map(s => `
      <li role="option" tabindex="-1" aria-selected="${s === currentSeason}" data-season="${s}" class="picker-option${s === currentSeason ? " is-active" : ""}">
        Season ${s}
      </li>
    `).join("");
  }
  buildSeasonOptions();

  // ── Episode selector ────────────────────────────────────────
  const episodeWrap = document.createElement("div");
  episodeWrap.className = "picker-wrap";
  episodeWrap.innerHTML = `
    <button class="picker-btn" type="button" aria-expanded="false" aria-haspopup="listbox">
      <span class="picker-value"></span>
      ${CHEVRON}
    </button>
    <ul class="picker-list picker-list--episodes" role="listbox" aria-label="Episode" hidden></ul>
  `;
  const episodeBtn = episodeWrap.querySelector(".picker-btn");
  episodeBtn.setAttribute("aria-label", "Choose episode");
  const episodeList = episodeWrap.querySelector(".picker-list");
  const episodeLabel = episodeWrap.querySelector(".picker-value");

  function buildEpisodeOptions(season) {
    const eps = seasonMap.get(season) ?? [];
    episodeList.innerHTML = eps.map(ep => {
      const isActive = ep.id === activeId;
      return `
        <li role="option" tabindex="-1" aria-selected="${isActive}" data-id="${ep.id}"
            class="picker-option${isActive ? " is-active" : ""}">
          <span class="picker-ep-code">Episode ${ep.episode}</span>
          <span class="picker-ep-title">${ep.title}</span>
        </li>
      `;
    }).join("");
  }

  function setEpisodeLabel(season) {
    const eps = seasonMap.get(season) ?? [];
    const active = eps.find(e => e.id === activeId) ?? eps[0];
    if (active) {
      episodeLabel.textContent = `Episode ${active.episode} · ${active.title}`;
    } else {
      episodeLabel.textContent = "Select episode";
    }
  }

  seasonValueEl.textContent = seasonLabel();
  buildEpisodeOptions(currentSeason);
  setEpisodeLabel(currentSeason);

  // ── Dropdown open/close ────────────────────────────────────
  function closeAll() {
    for (const [btn, list] of [[seasonBtn, seasonList], [episodeBtn, episodeList]]) {
      btn.setAttribute("aria-expanded", "false");
      list.hidden = true;
      btn.closest(".picker-wrap").classList.remove("is-open");
    }
  }

  function toggleDropdown(btn, list) {
    const isOpen = btn.getAttribute("aria-expanded") === "true";
    closeAll();
    if (!isOpen) {
      btn.setAttribute("aria-expanded", "true");
      list.hidden = false;
      btn.closest(".picker-wrap").classList.add("is-open");
      // Scroll active option into view
      const active = list.querySelector(".is-active");
      if (active) active.scrollIntoView({ block: "nearest" });
    }
  }

  seasonBtn.addEventListener("click", (e) => { e.stopPropagation(); toggleDropdown(seasonBtn, seasonList); });
  episodeBtn.addEventListener("click", (e) => { e.stopPropagation(); toggleDropdown(episodeBtn, episodeList); });

  // Keep the custom picker usable without a pointer.
  for (const [button, list] of [[seasonBtn, seasonList], [episodeBtn, episodeList]]) {
    function focusOption(index) {
      const options = [...list.querySelectorAll('[role="option"]')];
      options[Math.max(0, Math.min(index, options.length - 1))]?.focus();
    }
    button.addEventListener("keydown", (event) => {
      if (!["ArrowDown", "ArrowUp", "Home", "End", "Escape"].includes(event.key)) return;
      event.preventDefault();
      if (event.key === "Escape") { closeAll(); return; }
      if (list.hidden) toggleDropdown(button, list);
      const options = [...list.querySelectorAll('[role="option"]')];
      const selected = options.findIndex((option) => option.getAttribute("aria-selected") === "true");
      focusOption(event.key === "Home" ? 0 : event.key === "End" ? options.length - 1 : Math.max(0, selected));
    });
    list.addEventListener("keydown", (event) => {
      const options = [...list.querySelectorAll('[role="option"]')];
      const active = options.indexOf(document.activeElement);
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        focusOption(active + (event.key === "ArrowDown" ? 1 : -1));
      } else if (event.key === "Home" || event.key === "End") {
        event.preventDefault();
        focusOption(event.key === "Home" ? 0 : options.length - 1);
      } else if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        options[active]?.click();
        button.focus();
      } else if (event.key === "Escape") {
        event.preventDefault();
        closeAll();
        button.focus();
      } else if (event.key === "Tab") {
        closeAll();
      }
    });
  }

  // Remove this listener when the route is replaced.
  document.addEventListener("click", closeAll);
  container.destroy = () => document.removeEventListener("click", closeAll);

  // Season selection
  seasonList.addEventListener("click", (e) => {
    const option = e.target.closest(".picker-option");
    if (!option) return;
    const season = Number(option.dataset.season);
    if (season === currentSeason) { closeAll(); return; }
    currentSeason = season;
    saveStoredSeason(show.id, season);
    const firstEp = (seasonMap.get(season) ?? [])[0];
    if (firstEp) activeId = firstEp.id;
    seasonValueEl.textContent = seasonLabel();
    buildSeasonOptions();
    buildEpisodeOptions(season);
    setEpisodeLabel(season);
    closeAll();
    if (firstEp && onSelect) onSelect(firstEp);
  });

  // Episode selection → call onSelect callback, update active state in-place
  episodeList.addEventListener("click", (e) => {
    const option = e.target.closest(".picker-option");
    if (!option) return;
    const epId = option.dataset.id;
    const ep = show.episodes.find(ep => ep.id === epId);
    if (!ep) return;
    activeId = epId;
    buildEpisodeOptions(currentSeason);
    setEpisodeLabel(currentSeason);
    closeAll();
    if (onSelect) onSelect(ep);
  });

  container.append(seasonWrap, episodeWrap);
  return container;
}
